<script setup lang="ts">
import {type InferType, object, string} from "yup";
import type {FormSubmitEvent} from "@nuxt/ui";
import {useAuthStore} from "~~/stores/auth";
import LGForgottenPasswordForm from "~/components/user/LGForgottenPasswordForm.vue";

const props = withDefaults(defineProps<{
    redirectToProfile?: boolean
    initialEmail?: string
    notice?: string | null
}>(), {
    redirectToProfile: true,
    initialEmail: '',
    notice: ''
})

const { t } = useI18n()
const localePath = useLocalePath()
const auth = useAuthStore()
const router = useRouter()
const toast = useToast()
const showForgottenPassword = ref(false)

const schema = object({
    email: string().email(t('partials.form.emailInvalid')).required(t('partials.form.required')),
    password: string().required(t('partials.form.required')),
})

type Schema = InferType<typeof schema>

const state = reactive({
    email: props.initialEmail || '',
    password: undefined,
})

async function onLoginSubmit(payload: FormSubmitEvent<Schema>) {
    try {
        await auth.login(payload.data)
        if(props.redirectToProfile) {
            await router.push(localePath('moj-racun'))
        }
    } catch (error: any) {
        toast.add({
            title: t('userProfile.login.loginUnsuccessful'),
            description: error?.data?.message
                || error?.data?.errors?.email?.[0]
                || error?.data?.errors?.password?.[0]
                || error?.message
                || t('userProfile.login.loginError'),
            color: 'error'
        })
    }
}

watch(() => props.initialEmail, (v) => {
    if (v) state.email = v
})
</script>

<template>
    <div class="flex flex-col gap-4">
        <UAlert
            v-if="props.notice"
            color="warning"
            variant="subtle"
            :title="props.notice"
            class="text-center mb-4 bg-[#dcdcf7] text-secondary ring-secondary"
        />
        <UForm :schema="schema" :state="state" class="login-form space-y-8 z-1 w-full" @submit="onLoginSubmit">
            <div class="flex flex-col gap-4">
                <UFormField label="Elektronski naslov" name="email" class="flex-1">
                    <UInput v-model="state.email" :placeholder="t('userProfile.login.email')" class="w-full"/>
                </UFormField>
                <UFormField label="Geslo" name="password" class="flex-1">
                    <div class="w-full text-right">
                        <ULink @click="showForgottenPassword = true" class="text-[#CC4036] font-bold mr-4 cursor-pointer mb-1">{{ t('userProfile.login.forgottenPassword') }}</ULink>
                    </div>
                    <UInput v-model="state.password" type="password" :placeholder="t('userProfile.login.password')" class="w-full"/>
                </UFormField>
                <UButton type="submit" class="w-fit mt-6">{{ t('userProfile.login.loginButton') }}</UButton>
            </div>
        </UForm>
        <UModal v-model:open="showForgottenPassword" :dismissible="true"
                class="forgotten-password-form bg-[#faefee]">
            <template #header>
                <div class="flex flex-col items-center justify-center w-full">
                    <h3 class="uppercase text-center text-secondary text-[28px] leading-[38px]">{{ t('userProfile.login.forgottenPasswordModalTitle') }}</h3>
                    <span class="block text-center text-[#3D0703]">{{ t('userProfile.login.forgottenPasswordModalDescription') }}</span>
                </div>
            </template>
            <template #body>
                <LGForgottenPasswordForm @success="showForgottenPassword = false"/>
            </template>
        </UModal>
    </div>
</template>

<style>
.forgotten-password-form {
    .text-error {
        text-align: center !important;
    }

    button {
        margin: auto !important;
    }

    input:-webkit-autofill,
    input:-webkit-autofill:hover,
    input:-webkit-autofill:focus,
    input:-webkit-autofill:active{
        -webkit-background-clip: text;
        -webkit-text-fill-color: #222222;
        transition: background-color 5000s ease-in-out 0s;
        box-shadow: inset 0 0 20px 20px #F9D9D7;
    }
    input {
        background-color: #F9D9D7 !important;
        text-align: center !important;
        &:placeholder-shown {
            background-color: #ffffff !important;
        }
    }
}
.login-form {
    input:-webkit-autofill,
    input:-webkit-autofill:hover,
    input:-webkit-autofill:focus,
    input:-webkit-autofill:active{
        -webkit-background-clip: text;
        -webkit-text-fill-color: #222222;
        transition: background-color 5000s ease-in-out 0s;
        box-shadow: inset 0 0 20px 20px #F9D9D7;
    }
    input {
        background-color: #F9D9D7 !important;
        &:placeholder-shown {
            background-color: #ffffff !important;
        }
    }
}
</style>
